import { useRef } from 'react';
import isEqual from 'lodash/isEqual';

type CompareFunction<T> = (prev: T, next: T) => boolean;

/**
 * A custom hook that memoizes a value and compares it with the previous value using a custom comparator.
 * @param value
 * @param compare
 */
export function useMemoCompare<T>(
  value: T,
  compare: CompareFunction<T> = isEqual
): T {
  // Use a flag to track whether we have a cached value
  const hasCached = useRef(false);
  const cached = useRef<T>();

  // If a cached value exists and the comparator deems them equal, return it.
  if (hasCached.current && compare(cached.current as T, value)) {
    return cached.current as T;
  } else {
    // Otherwise, update the cache and set the flag.
    cached.current = value;
    hasCached.current = true;
    return value;
  }
}
